"use client";

import posthog from "posthog-js";
import { useEffect } from "react";
import type { ReaderReportType } from "@/lib/reports/report-type";

export type ExploreContentType =
  | "explore"
  | "stories_index"
  | "story_detail"
  | "tools_index"
  | "tool_detail"
  | "category"
  | "filters"
  | "tags_index"
  | "tag_detail"
  | "other";

export interface ExplorePageAnalyticsProps {
  edition: ReaderReportType;
  contentType: ExploreContentType;
  storySlug?: string | null;
  storyCategory?: string | null;
  storyLifecycle?: string | null;
  storyMaterialType?: string | null;
  storyReportDate?: string | null;
  toolSlug?: string | null;
  categorySlug?: string | null;
  tagSlug?: string | null;
}

const PAGE_SPECIFIC_KEYS = [
  "content_type",
  "story_slug",
  "story_category",
  "story_lifecycle",
  "story_material_type",
  "story_report_date",
  "tool_slug",
  "category_slug",
  "tag_slug",
] as const;

const buildPayload = (props: ExplorePageAnalyticsProps) => {
  const payload: Record<string, unknown> = {
    edition: props.edition,
    content_type: props.contentType,
  };
  if (props.storySlug) {
    payload.story_slug = props.storySlug;
  }
  if (props.storyCategory) {
    payload.story_category = props.storyCategory;
  }
  if (props.storyLifecycle) {
    payload.story_lifecycle = props.storyLifecycle;
  }
  if (props.storyMaterialType) {
    payload.story_material_type = props.storyMaterialType;
  }
  if (props.storyReportDate) {
    payload.story_report_date = props.storyReportDate;
  }
  if (props.toolSlug) {
    payload.tool_slug = props.toolSlug;
  }
  if (props.categorySlug) {
    payload.category_slug = props.categorySlug;
  }
  if (props.tagSlug) {
    payload.tag_slug = props.tagSlug;
  }
  return payload;
};

const EVENT_NAMES = {
  explore: "explore_viewed",
  stories_index: "stories_index_viewed",
  story_detail: "story_viewed",
  tools_index: "tools_index_viewed",
  tool_detail: "tool_viewed",
  category: "category_viewed",
  filters: "filters_viewed",
  tags_index: "tags_index_viewed",
  tag_detail: "tag_viewed",
  other: "explore_content_viewed",
} satisfies Record<ExploreContentType, string>;

/**
 * Registers edition + content context with PostHog so subsequent events in the
 * session carry them, then fires a semantic `*_viewed` event with the full
 * payload — the auto `$pageview` has already fired without these properties.
 *
 * Page-specific keys are unregistered on unmount so a Story → Explore transition
 * doesn't leak `story_slug`. `edition` intentionally persists: it stays valid
 * until the user navigates to the other edition, at which point the next mount
 * overwrites it.
 */
export function ExplorePageAnalytics({
  edition,
  contentType,
  storySlug = null,
  storyCategory = null,
  storyLifecycle = null,
  storyMaterialType = null,
  storyReportDate = null,
  toolSlug = null,
  categorySlug = null,
  tagSlug = null,
}: ExplorePageAnalyticsProps) {
  useEffect(() => {
    if (typeof window === "undefined") {
      return;
    }
    const payload = buildPayload({
      edition,
      contentType,
      storySlug,
      storyCategory,
      storyLifecycle,
      storyMaterialType,
      storyReportDate,
      toolSlug,
      categorySlug,
      tagSlug,
    });
    posthog.register(payload);
    posthog.capture(EVENT_NAMES[contentType], payload);

    return () => {
      for (const key of PAGE_SPECIFIC_KEYS) {
        posthog.unregister(key);
      }
    };
  }, [
    edition,
    contentType,
    storySlug,
    storyCategory,
    storyLifecycle,
    storyMaterialType,
    storyReportDate,
    toolSlug,
    categorySlug,
    tagSlug,
  ]);

  return null;
}
